import React, { useEffect, useState, useRef } from 'react';
import RouteHead from './components/RouteHead/RouteHead';
import { Link } from 'react-router-dom';
import logoAndLetters from './assets/logo_and_letters.png';
import appLogo from './assets/app-logo.png';
import phoneMap from './assets/hero-phone-map.webp';
import phoneFeed from './assets/hero-phone-feed.webp';
import phoneTrip from './assets/hero-phone-trip.webp';
import screenWorldMap from './assets/screen-world-map.webp';
import screenTripPlanner from './assets/screen-trip-planner.webp';
import screenTripInvite from './assets/screen-trip-invite.webp';
import screenTripChat from './assets/screen-trip-chat.webp';
import screenFeed from './assets/screen-feed.webp';
import screenProfileTimeline from './assets/screen-profile-timeline.webp';
import screenTravelStats from './assets/screen-travel-stats.webp';
import Navbar from './components/Navbar/Navbar';
import globeFlags from './assets/globe-flags.webp';
import useCountUp from './hooks/useCountUp';
import { countryByCode, flagSrc } from './utils/countries';

import Footer from './components/Footer/Footer';
import GetAppModal from './components/GetAppModal/GetAppModal';
import { WEB_APP_URL } from './utils/webApp';
import './LandingPage.css'; // Import CSS

const TOTAL_COUNTRIES = 195;

/**
 * Shown in the map teaser's picker. A recognisable spread across continents,
 * so most visitors can tap at least one without hunting. The few names too
 * long for a chip carry a short label, because three columns of pills leave roughly
 * 100px for text, and an ellipsis reads worse than the common short form.
 */
const TEASER_COUNTRIES = [
  { code: 'PT' }, { code: 'ES' }, { code: 'FR' },
  { code: 'IT' }, { code: 'GB', label: 'UK' }, { code: 'DE' },
  { code: 'US', label: 'USA' }, { code: 'MX' }, { code: 'BR' },
  { code: 'MA' }, { code: 'ZA', label: 'S. Africa' }, { code: 'JP' },
  { code: 'TH' }, { code: 'AU' }, { code: 'GR' }, { code: 'NL' },
];

/**
 * The benefits rail. One entry per app screen, in the order a traveller meets
 * them: log where you have been, plan the next trip, bring people into it,
 * then the social and analytical layers on top.
 *
 * `premium` tags the rows that are not free. Both are real Premium features,
 * and the section sits on a page that says "Free to start", so leaving them
 * unmarked would imply they are included.
 */
const BENEFITS = [
  {
    eyebrow: '01 · Track',
    title: 'Map your visited countries',
    body:
      "Add the countries you've been to, or let VOIA find them in your photos: it reads where and " +
      'when each photo was taken, right on your phone, and you approve every visit. Watch your map ' +
      "fill in, with your country count and the percentage of the world you've covered.",
    img: screenWorldMap,
    alt: 'VOIA world map with flags on every visited country',
  },
  {
    eyebrow: '02 · Plan',
    title: 'Plan your next trip',
    body:
      'Lay out a trip on one timeline: day visits, stays, transport, tours and expenses, all ' +
      'in the same place and always up to date for everyone coming.',
    img: screenTripPlanner,
    alt: 'A VOIA trip itinerary laid out day by day',
  },
  {
    eyebrow: '03 · Collaborate',
    title: 'Invite friends to your trips',
    body:
      'Share an invite link or add people by username, then decide who edits the itinerary and ' +
      'who just follows along.',
    img: screenTripInvite,
    alt: 'VOIA trip participants list with editor and viewer roles',
  },
  {
    eyebrow: '04 · Chat',
    premium: '@voia is Premium',
    title: 'Plan together in the trip chat',
    body:
      'Every trip gets its own group chat. Tag @voia and the assistant answers with your ' +
      'itinerary already in mind, so you never have to re-explain where you are going or when.',
    img: screenTripChat,
    alt: 'A VOIA trip chat with the @voia assistant answering a question',
  },
  {
    eyebrow: '05 · Discover',
    title: 'See where your friends are travelling',
    body:
      'Follow other travellers and watch their trips appear as they happen, with the places, ' +
      'maps and photos behind them.',
    img: screenFeed,
    alt: 'The VOIA feed showing recent trips from other travellers',
  },
  {
    eyebrow: '06 · Your profile',
    title: 'One place for your whole travel story',
    body:
      'Your profile holds the lot: countries visited, your top five, every trip, and a timeline ' +
      'of where you went and when.',
    img: screenProfileTimeline,
    alt: 'A VOIA profile showing visited countries and a travel timeline',
  },
  {
    eyebrow: '07 · Stats',
    premium: 'Premium',
    title: 'Understand your travel in detail',
    body:
      'Favourite countries, how much of each continent you have covered, cities and capitals ' +
      'reached, and where you land against your friends.',
    img: screenTravelStats,
    alt: 'VOIA travel statistics broken down by country and continent',
  },
];

const LandingPage = () => {
  // Only the country-visit total is shown. The document carries usersCount and
  // tripsCount too, so bringing another figure back is a one-line change.
  const [stats, setStats] = useState({ countryVisits: null });
  const [getAppOpen, setGetAppOpen] = useState(false);
  // Only the setters are needed: the hero hides its proof row until real
  // numbers arrive, so there is no loading or error state to render.
  const [, setStatsLoading] = useState(true);
  const [, setStatsError] = useState('');
  const [activeBenefit, setActiveBenefit] = useState(0);
  const [teaserPicks, setTeaserPicks] = useState(() => new Set());
  const benefitRefs = useRef([]);

  useEffect(() => {
    const controller = new AbortController();
    const fetchStats = async () => {
      const projectId = process.env.REACT_APP_MOBILE_FIREBASE_PROJECT_ID || 'voia-travel-prod';

      if (!projectId) {
        setStatsError('Missing Firebase project configuration.');
        setStatsLoading(false);
        return;
      }

      try {
        const response = await fetch(
          `https://firestore.googleapis.com/v1/projects/${projectId}/databases/(default)/documents/public/stats`,
          {
            signal: controller.signal,
          },
        );

        if (!response.ok) {
          throw new Error(`Unable to load stats (${response.status})`);
        }

        const data = await response.json();
        const fields = data.fields || {};

        const getNumberValue = (field) => {
          if (!field) return 0;
          if (field.integerValue !== undefined) return Number(field.integerValue);
          if (field.doubleValue !== undefined) return Number(field.doubleValue);
          return 0;
        };

        setStats({ countryVisits: getNumberValue(fields.countryVisitsCount) });
        setStatsError('');
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('Error fetching stats', error);
          setStatsError('Unable to load live stats right now.');
        }
      } finally {
        setStatsLoading(false);
      }
    };

    fetchStats();

    return () => controller.abort();
  }, []);

  // Scroll-triggered benefit visual switching
  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: '-40% 0px -40% 0px',
      threshold: 0,
    };

    const observerCallback = (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const index = benefitRefs.current.indexOf(entry.target);
          if (index !== -1) {
            setActiveBenefit(index);
          }
        }
      });
    };

    const observer = new IntersectionObserver(observerCallback, observerOptions);

    // Wait for refs to be populated
    const refs = benefitRefs.current.filter(Boolean);
    refs.forEach((ref) => {
      observer.observe(ref);
    });

    return () => {
      refs.forEach((ref) => {
        observer.unobserve(ref);
      });
    };
  }, []);

  const formatStatValue = (value) => {
    if (value === null || value === undefined) return '–';
    return value.toLocaleString();
  };

  const countryVisitsDisplay = useCountUp(stats.countryVisits ?? 0);

  // Only surface live numbers in the hero once they exist, so the section never
  // renders a placeholder or a zero where social proof is supposed to be.
  const hasHeroStats = stats.countryVisits !== null;

  const teaserCountries = TEASER_COUNTRIES.map(({ code, label }) => {
    const country = countryByCode(code);
    return country ? { ...country, label: label || country.name } : null;
  }).filter(Boolean);

  const toggleTeaserPick = (code) => {
    setTeaserPicks((current) => {
      const next = new Set(current);
      if (!next.delete(code)) next.add(code);
      return next;
    });
  };

  const teaserCount = teaserPicks.size;
  // Kept to one decimal: at 195 countries a single pick is 0.5%, and rounding
  // to integers would show "0%" for the first tap the visitor makes.
  const teaserPercent = ((teaserCount / TOTAL_COUNTRIES) * 100).toFixed(1);

  // The map page reads ?c= on load (see VisitedMapPage), so the picks made here
  // carry straight over and the globe opens already filled in.
  const teaserHref = teaserCount
    ? `/visited-countries-map?c=${Array.from(teaserPicks).join(',')}`
    : '/visited-countries-map';

  return (
    <div className="container">
      <RouteHead path="/" />
      {/* Hero */}
      <section id="home" className="hero">
        <div className="heroBackdrop" aria-hidden="true" />

        <Navbar variant="transparent" />

        <div className="heroInner">
          <div className="heroCopy">
            <span className="heroEyebrow">
              <span className="heroEyebrowDot" aria-hidden="true" />
              Free to start · iOS, Android and web
            </span>

            <h1 className="heroTitle">
              Map your <span className="heroTitleAccent">travel journey</span>
            </h1>

            <p className="heroSubtitle">
              Track every country you&apos;ve visited, plan where you&apos;re going next,
              and see where your friends have been.
            </p>

            <div className="heroActions" data-track-location="hero">
              {/* Desktop: the web app is the thing they can use on this screen, and the
                  QR code gets the app onto their phone. Phones get the store badges. */}
              <a href={WEB_APP_URL} className="heroWebCta onlyDesktop">
                Plan on the web
              </a>
              <button type="button" className="heroGetApp onlyDesktop" onClick={() => setGetAppOpen(true)}>
                Get the app
              </button>
              <a
                href="https://apps.apple.com/pt/app/voia-travel/id6747190886?l=en-GB"
                target="_blank"
                rel="noopener noreferrer"
                className="heroStoreLink onlyTouch"
              >
                <img src="/assets/app_store_badge.svg" alt="Download VOIA on the App Store" className="heroStoreBadge" />
              </a>
              <a
                href="https://play.google.com/store/apps/details?id=com.voia.travel.mobile"
                target="_blank"
                rel="noopener noreferrer"
                className="heroStoreLink onlyTouch"
              >
                <img src="/assets/google_play_badge.png" alt="Get VOIA on Google Play" className="heroStoreBadge" />
              </a>
            </div>

            {hasHeroStats && (
              <p className="heroProof">
                <span className="heroProofValue">{formatStatValue(countryVisitsDisplay)}</span>
                <span className="heroProofCaption">
                  country {countryVisitsDisplay === 1 ? 'visit' : 'visits'} already mapped by
                  travellers on VOIA
                </span>
              </p>
            )}
          </div>

          <div className="heroShowcase">
            <div className="heroShowcaseGlow" aria-hidden="true" />
            <img
              src={phoneFeed}
              alt="VOIA feed showing where friends have travelled recently"
              className="heroPhone heroPhone--left"
              width="900"
              height="1878"
              loading="eager"
              decoding="async"
            />
            <img
              src={phoneTrip}
              alt="VOIA trip planner with a day-by-day itinerary"
              className="heroPhone heroPhone--right"
              width="900"
              height="1878"
              loading="eager"
              decoding="async"
            />
            <img
              src={phoneMap}
              alt="VOIA world map showing visited countries marked with flags"
              className="heroPhone heroPhone--center"
              width="900"
              height="1878"
              loading="eager"
              fetchpriority="high"
              decoding="async"
            />
          </div>
        </div>
      </section>

      {/* The live counters now live in the hero, so the standalone stats
          section was showing the same three numbers twice in one scroll. Its
          heading moved down here instead — it was the page's only <h2>, and
          the benefits list had no heading of its own. */}

      <div className="sectionDivider sectionDivider--mutedToWhite" />

      {/* Value Proposition Section */}
      <section className="valueSection">
        <h2 className="valueSectionTitle">
          If you visited, it should be on <span className="valueSectionBrand">VOIA</span>
        </h2>
        <div className="valueContainer">
          <div className="valueList">
            {BENEFITS.map((benefit, index) => (
              <div
                className="benefitRow"
                key={benefit.eyebrow}
                ref={(el) => (benefitRefs.current[index] = el)}
              >
                <div className="benefitCopy">
                  <p className="benefitEyebrow">
                    {benefit.eyebrow}
                    {benefit.premium && (
                      <span className="benefitPremiumTag">{benefit.premium}</span>
                    )}
                  </p>
                  <h3 className="benefitTitle">{benefit.title}</h3>
                  <p className="benefitBody">{benefit.body}</p>
                </div>
                <div className="benefitVisualMobile">
                  <div className="benefitVisualContainer">
                    <div className="benefitMockup active">
                      <img
                        src={benefit.img}
                        alt={benefit.alt}
                        className="benefitScreenshot"
                        loading="lazy"
                        decoding="async"
                      />
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="benefitVisualSticky">
            <div className="benefitVisualContainer">
              {BENEFITS.map((benefit, index) => (
                <div
                  key={benefit.eyebrow}
                  className={`benefitMockup ${activeBenefit === index ? 'active' : ''}`}
                >
                  <img
                    src={benefit.img}
                    alt={benefit.alt}
                    className="benefitScreenshot"
                    loading="lazy"
                    decoding="async"
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <div className="sectionDivider sectionDivider--lightToMuted" />

      {/* The map demo and the download CTA share one dark ground: the section
          below is the final conversion moment, and on its own light background
          it read as a cliff after the map. One block, demo then convert. */}
      <div className="closingBlock">
      {/* Map teaser. The section's job is to make the payoff visible, so the
          world itself is the visual: one dot per country, lighting up as you
          tap. Dark ground because the selected dots have to glow. */}
      <section id="try-the-map" className="mapTeaser">
        <div className="mapTeaserGlow" aria-hidden="true" />

        <div className="mapTeaserInner">
          <div className="mapTeaserIntro">
            <span className="mapTeaserEyebrow">Try it now</span>

            <h2 className="mapTeaserTitle">
              Where have <span className="mapTeaserTitleAccent">you</span> been?
            </h2>

            <p className="mapTeaserBody">
              Pick the countries you&apos;ve been to and see how much of the world you have
              covered. No account, no app. Then open the real 3D globe to place them all
              and share it.
            </p>

          </div>

          <div className="mapTeaserAction">
            <div className="mapTeaserReadout">
              <div className="mapTeaserStat">
                <span className="mapTeaserStatValue">{teaserCount}</span>
                <span className="mapTeaserStatLabel">Countries</span>
              </div>
              <span className="mapTeaserStatDivider" aria-hidden="true" />
              <div className="mapTeaserStat">
                <span className="mapTeaserStatValue">{teaserPercent}%</span>
                <span className="mapTeaserStatLabel">Of the world</span>
              </div>
            </div>

            <Link to={teaserHref} className="mapTeaserCta">
              {teaserCount
                ? `See my ${teaserCount} ${teaserCount === 1 ? 'country' : 'countries'} on the globe`
                : 'Open the map builder'}
              <span className="mapTeaserCtaArrow" aria-hidden="true">→</span>
            </Link>
          </div>

          <img
            src={globeFlags}
            alt="A VOIA globe with a flag on every visited country"
            className="mapTeaserGlobe"
            width="1000"
            height="1000"
            loading="lazy"
            decoding="async"
          />

          <div className="mapTeaserQuick">
            <span className="mapTeaserQuickLabel">Quick pick</span>
            <div className="mapTeaserGrid">
              {teaserCountries.map((country) => (
                <button
                  key={country.code}
                  type="button"
                  className="mapTeaserPick"
                  aria-pressed={teaserPicks.has(country.code)}
                  aria-label={country.name}
                  onClick={() => toggleTeaserPick(country.code)}
                >
                  <img
                    src={flagSrc(country)}
                    alt=""
                    className="mapTeaserPickFlag"
                    width="20"
                    height="20"
                    loading="lazy"
                    decoding="async"
                  />
                  <span className="mapTeaserPickName">{country.label}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Download / Stay Updated Section */}
      <section id="stayUpdated" className="stayUpdatedSection">
        <h3 className="sectionTitle">Get the App</h3>
        <div className="waiting-list-intro">
          <div className="waiting-list-logo">
            <img src={appLogo} alt="VOIA Logo" className="app-logo" />
          </div>
          <p className="sectionText">
            Available now on iOS and Android. Free to download.
            Start mapping your travel journey today.
          </p>
        </div>
        <div className="storeBadgesContainer">
          <a href="https://apps.apple.com/pt/app/voia-travel/id6747190886?l=en-GB" target="_blank" rel="noopener noreferrer">
            <img src="/assets/app_store_badge.svg" alt="Download on the App Store" className="storeBadgeSection" />
          </a>
          <a href="https://play.google.com/store/apps/details?id=com.voia.travel.mobile" target="_blank" rel="noopener noreferrer">
            <img src="/assets/google_play_badge.png" alt="Get it on Google Play" className="storeBadgeSection" />
          </a>
        </div>
      </section>

      </div>

      {/* Footer */}
      <Footer variant="lightblue" />
      <GetAppModal open={getAppOpen} onClose={() => setGetAppOpen(false)} />
    </div>
  );
};

export default LandingPage;